import React, { useEffect, FormEvent } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CustomBackdrop, NavHeader, Page, SubmitButton } from '../../../../../../components';
import {
  ChannelTypeInternal,
  MeterTypeInternal,
  VersionedFeatureType,
} from '../../../../../../data-access/gql-types/graphql';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { useGetTariffs } from '../../../../hooks/tariffs/use-get-tariffs';
import { useCheckDeviceSupport } from '../../../../hooks/version-map/use-check-device-support';
import { ChannelMeterTypeOrUndefined } from '../../../../types';
import { useGetChannelDetails } from '../../../channel-details/hooks/use-get-channel-details';
import { RouteParams } from '../../../channel-details/types';
import { TariffForm } from '../../../common/tariff';
import { TariffPrefix } from '../../../common/tariff/types';
import { useTariffConfigurationContext } from '../../context/tariff-configuration-context';
import { useMeterTariffConfiguration } from '../../hooks/use-meter-tariff-configuration';
import { useMeterTariffPricesConfiguration } from '../../hooks/use-meter-tariff-prices-configuration';
import { usePartialConfigChanged } from '../../hooks/use-partial-config-changed';
import { TariffPresentation } from '../tariff-presentation/tariff-presentation';
import TariffPrices from '../tariff-prices';

const TariffConfigurationForm: React.FC = () => {
  const { channelId } = useParams<RouteParams>();
  const { t } = useTranslation('configuration');
  const { channel, getDeviceById } = useDevicesAndChannelsContext();
  const ChannelId = channelId ?? '';
  const { loading } = useGetChannelDetails(ChannelId);
  const { form, formValues } = useTariffConfigurationContext();
  const { setMeterTariffData, handleSubmitTariffData } = useMeterTariffConfiguration();
  const { handleSetPrices } = useMeterTariffPricesConfiguration();
  const { checkSupport } = useCheckDeviceSupport();
  const { allTariffs } = useGetTariffs();
  const channelTariff = (channel?.data as ChannelMeterTypeOrUndefined)?.tariff;
  const device = getDeviceById(channel?.deviceId);
  const tariffConfigurations = (channel?.data as ChannelMeterTypeOrUndefined)?.sharedChannelSettings
    ?.tariffConfigurations;

  useEffect(() => {
    const isFormPristine = !form.getValues().operator && !form.getValues().tariffName;

    if (channelTariff && isFormPristine) {
      setMeterTariffData(channelTariff.kind, allTariffs, handleSetPrices);
    }
  }, [channelTariff?.kind, tariffConfigurations, allTariffs]);

  const isStandalone =
    channel?.data?.type === ChannelTypeInternal.Meter && channel?.data?.meterType === MeterTypeInternal.Standalone;

  const { setTimeoutError } = usePartialConfigChanged({ channelId: ChannelId });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleSubmitTariffData(setTimeoutError);
  };

  const g13sSupported = checkSupport({
    feature: VersionedFeatureType.G13STariff,
    supportKey: device?.payload.model,
    currentVersion: channel?.currentFirmwareVersion,
  });

  return (
    <Page containerClassName="page__container--flex-height" header={<NavHeader title={t('tariffsConfiguration')} />}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <TariffForm
            customTariff={false}
            isDouble={!isStandalone}
            {...(!isStandalone && {
              availableTariffs: [TariffPrefix.G11, TariffPrefix.Dynamic, ...(g13sSupported ? [TariffPrefix.G13S] : [])],
            })}
            hidePricesInfo
            handleSetPrices={handleSetPrices}
          />
          {formValues.prices.length ? (
            <>
              <TariffPrices />
              <hr className="m-b-24" />
            </>
          ) : null}

          <SubmitButton />
        </form>
        <TariffPresentation />
      </FormProvider>
      <CustomBackdrop loading={loading} />
    </Page>
  );
};

export default TariffConfigurationForm;
